import moment from "moment";
import useTableSort from "../../../hooks/useTableSort";
import {
  blockRackTableDataValidation,
  getTimeBasedOnUserTime,
} from "../../../util/helper/helperFunctions";
import { memo, useEffect, useMemo, useState } from "react";
import useTableSearch from "../../../hooks/useTableSearch";
import SortableSearchableHeader from "./SortableSearchableHeader";
import NoResult from "./NoResult";

const DataTable = ({
  dataTestID,
  id,
  columns,
  data,
  updateNonLgvExportData,
  setClearFiltersFn,
  setShowClearFilter,
}) => {
  //Transforming raw data to view form to maintain data consistency for searching and export
  const tableData = useMemo(() => {
    const normalizedData = [];

    for (let item of data) {
      let obj = {};

      for (let { key } of columns) {
        obj[key] =
          key === "productionDate" || key === "expirationDate"
            ? !!blockRackTableDataValidation(item[key])
              ? moment(getTimeBasedOnUserTime(item[key])).format(
                  "YYYY-MM-DD hh:mm:ss"
                )
              : ""
            : blockRackTableDataValidation(item[key]);
      }
      normalizedData.push(obj);
    }

    return normalizedData;
  }, [data, columns]);

  /*
  Flow of param between hook : tableData => useTableSearch &&  filteredResult => useTableSort 
  Filtering first with tableData, then sorting with filteredData — regardless of user action order.
  */

  const { searchFilters, updateFilter, clearFilters, filteredData } =
    useTableSearch(tableData);

  const { requestSort, sortConfig, sortedData } = useTableSort(filteredData);

  useEffect(() => {
    updateNonLgvExportData(sortedData);
  }, [sortedData]);

  const [activeColumnSearch, setActiveColumnSearch] = useState(null);

  useEffect(() => {
    setClearFiltersFn(() => () => {
      clearFilters();
      requestSort(null);
    });
  }, [clearFilters, requestSort]);

  useEffect(() => {
    if (!tableData.length) return;
    const isSortOrSearchApplied =
      Object.values(searchFilters).some((val) => val) || sortConfig.key;
    setShowClearFilter(isSortOrSearchApplied);
  }, [searchFilters, sortConfig.key]);

  return (
    <table data-testid={dataTestID} id={id}>
      <thead>
        <tr>
          {columns.map(({ label, key, enableSortSearch }, index) => (
            <SortableSearchableHeader
              key={index}
              index={index}
              field={key}
              label={label}
              sortConfig={sortConfig}
              searchFilters={searchFilters}
              activeColumnSearch={activeColumnSearch}
              setActiveColumnSearch={setActiveColumnSearch}
              enableSortSearch={enableSortSearch}
              requestSort={requestSort}
              updateFilter={updateFilter}
            />
          ))}
        </tr>
      </thead>
      <tbody>
        {!sortedData.length && tableData.length ? (
          <NoResult colSpan={columns.length} />
        ) : (
          sortedData?.map((row, index) => (
            <tr key={index}>
              {Object.values(row).map((value) => (
                <td className="block-table-data-block-rack">{value}</td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

export default memo(DataTable);
